import React from 'react';
import { Box, Tooltip, FormControl, Select, MenuItem } from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import LinkIcon from '@mui/icons-material/Link';
import EventSummaryCard from '../../../components/reporting/EventSummaryCard';

export default function ByEventReport(props) {
  const { selectedEvent, eventsScores, categoriesMap, divisionsMap, byEventRows, eventHasAdjustments, teamModes, setTeamModes, setAutoAppliedModes, teams, setManualOverrides, topN = 0, includeTies = true, groupedTopResults = {} } = props;

  // Detect whether this specific event actually has any adjusted values (per-event),
  // fallback to the global flag. This ensures Adjusted Total shows whenever any row
  // in the selected event has a different associated score.
  const eventHasAdjustmentsLocal = React.useMemo(() => {
    if (eventHasAdjustments) return true;
    if (!Array.isArray(byEventRows)) return false;
    return byEventRows.some(r => (Number.isFinite(r.finalScoreAssociated) && r.finalScoreAssociated !== r.finalScoreRaw));
  }, [eventHasAdjustments, byEventRows]);

  const byEventColumns = React.useMemo(() => {
    const cols = [
      { field: 'teamNumber', headerName: 'Team Number', width: 125 },
      { field: 'teamName', headerName: 'Team Name', width: 250 },
      { field: 'category', headerName: 'Category', width: 150 },
      { field: 'division', headerName: 'Division', width: 150 },
      { field: 'affiliated', headerName: 'Affiliated', width: 120, renderCell: (params) => {
        const teamUid = params.row?.teamUid || params.id;
        // current event association
        const currentEvent = eventsScores && eventsScores[selectedEvent];
        const assocThis = (currentEvent?.event?.associatedTeams || []).map(a => (typeof a === 'string' ? a : a.uid));
        const isAffiliatedThis = assocThis.includes(teamUid);

        // find other events where this team is associated
        const otherEventUids = Object.keys(eventsScores || {}).filter(evUid => {
          if (evUid === selectedEvent) return false;
          const assoc = (eventsScores[evUid]?.event?.associatedTeams || []).map(a => (typeof a === 'string' ? a : a.uid));
          return assoc.includes(teamUid);
        });

        const otherEventLabels = otherEventUids.map(euid => {
          const ev = eventsScores[euid];
          const num = ev?.event?.number;
          const name = ev?.event?.name;
          return num ? `#${num} ${name || ''}`.trim() : (name || euid);
        });

        const isAffiliatedAny = isAffiliatedThis || otherEventLabels.length > 0;

        let title = 'Not affiliated';
        if (isAffiliatedThis && otherEventLabels.length > 0) {
          title = `Affiliated with this event. Also affiliated with: ${otherEventLabels.join(', ')}`;
        } else if (isAffiliatedThis) {
          title = 'Affiliated with this event';
        } else if (otherEventLabels.length > 0) {
          title = `Affiliated with other events: ${otherEventLabels.join(', ')}`;
        }

        if (isAffiliatedThis) {
          return (
            <Tooltip title={title}>
              <span><CheckCircleIcon sx={{ color: 'green' }} /></span>
            </Tooltip>
          );
        }

        if (otherEventLabels.length > 0) {
          return (
            <Tooltip title={title}>
              <span><LinkIcon sx={{ color: 'orange' }} /></span>
            </Tooltip>
          );
        }

        return (
          <Tooltip title={title}>
            <span><CancelIcon sx={{ color: 'red' }} /></span>
          </Tooltip>
        );
      } },
      {
        field: 'mode', headerName: 'Mode', width: 220, renderCell: (params) => {
          const teamUid = params.row?.teamUid || params.id;
          // determine whether this team is affiliated with any event (authoritative)
          const isAffiliatedAny = Object.keys(eventsScores || {}).some(evUid => {
            const assoc = (eventsScores[evUid]?.event?.associatedTeams || []).map(a => (typeof a === 'string' ? a : a.uid));
            return assoc.includes(teamUid);
          });

          const value = teamModes[teamUid] || 'normal';
          return (
            <FormControl variant="standard" sx={{ minWidth: 180 }}>
              <Select
                value={value}
                onChange={(e) => {
                  const v = e.target.value;
                  if (setManualOverrides) {
                    setManualOverrides(prev => ({ ...(prev || {}), [teamUid]: true }));
                  }
                  if (setAutoAppliedModes) {
                    setAutoAppliedModes(prev => {
                      const copy = { ...(prev || {}) };
                      delete copy[teamUid];
                      return copy;
                    });
                  }
                  setTeamModes(prev => ({ ...prev, [teamUid]: v }));
                }}
                size="small"
              >
                <MenuItem value="normal">Normal</MenuItem>
                <MenuItem value="total_average">Total Average</MenuItem>
                <MenuItem value="affiliated_average" disabled={!isAffiliatedAny}>Affiliated Average (if below avg)</MenuItem>
                <MenuItem value="affiliated_max" disabled={!isAffiliatedAny}>Affiliated Max (worst score)</MenuItem>
                <MenuItem value="affiliated_up_next" disabled={!isAffiliatedAny}>Affiliated Up Next (next non-affiliated score)</MenuItem>
                <MenuItem value="exclude" disabled={!isAffiliatedAny}>Affiliated Exclude (set to 1500)</MenuItem>
              </Select>
            </FormControl>
          );
        }
      },
      { field: 'finalScoreRaw', headerName: 'Overall Total', width: 140 },
    ];

    if (eventHasAdjustmentsLocal) {
      cols.push({ field: 'finalScoreAdjusted', headerName: 'Adjusted Total', width: 160, renderHeader: () => (
        <Tooltip title="Adjusted by per-team Mode or affiliated rules"><span>Adjusted Total</span></Tooltip>
      ) });
    }

    cols.push({ field: 'place', headerName: 'Overall Place', width: 120 });

    if (eventHasAdjustmentsLocal) {
      cols.push({ field: 'placeAssociated', headerName: 'Adjusted Place', width: 140, renderHeader: () => (
        <Tooltip title="Adjusted by per-team Mode or affiliated rules"><span>Adjusted Place</span></Tooltip>
      ) });
    }

    return cols;
  }, [eventHasAdjustmentsLocal, teamModes, setTeamModes, setAutoAppliedModes, eventsScores, selectedEvent, teams, setManualOverrides, byEventRows]);

  if (!selectedEvent) return <div>Please select an event</div>;

  const event = eventsScores[selectedEvent];
  if (!event) return <div>Event not found</div>;

  // apply topN filtering (tie-safe) using place or placeAssociated
  const placeKey = eventHasAdjustmentsLocal ? 'placeAssociated' : 'place';
  let displayedRows = Array.isArray(byEventRows) ? [...byEventRows] : [];

  // Prefer precomputed groupedTopResults for events when available (ensures consistent placeKey usage)
  if (topN && topN > 0 && groupedTopResults && groupedTopResults.event && Array.isArray(groupedTopResults.event[selectedEvent])) {
    displayedRows = groupedTopResults.event[selectedEvent];
  } else if (topN && topN > 0) {
    const rowsWithPlace = displayedRows.filter(r => Number.isFinite(r[placeKey]));
    rowsWithPlace.sort((a, b) => (a[placeKey] - b[placeKey]));
    if (rowsWithPlace.length > 0) {
      const nth = rowsWithPlace[Math.min(topN - 1, rowsWithPlace.length - 1)][placeKey];
      if (includeTies) {
        displayedRows = rowsWithPlace.filter(r => r[placeKey] <= nth);
      } else {
        displayedRows = rowsWithPlace.slice(0, topN);
      }
    } else {
      displayedRows = [];
    }
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box sx={{ flex: 0.75 }}>
          <EventSummaryCard event={event} categoriesMap={categoriesMap} divisionsMap={divisionsMap} showTitle={true} teamsMap={teams} />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Box sx={{ flex: 1 }}>
          <DataGrid
            key={eventHasAdjustmentsLocal ? 'adjusted' : 'original'}
            rows={displayedRows}
            columns={byEventColumns}
            slots={{ toolbar: GridToolbar }}
            pageSizeOptions={[25, 50, 100]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } }, sorting: { sortModel: [{ field: (eventHasAdjustmentsLocal ? 'placeAssociated' : 'place'), sort: 'asc' }] } }}
          />
        </Box>
      </Box>
    </Box>
  );
}
